/**
 * MetadataSidebar: structured metadata panel for the event permalink.
 *
 * Renders all available fields from AlertDetail in a definition list:
 *   - Alert type, severity (badge), zone, fingerprint, device type, confidence
 *   - RSSI summary (min, max, median from waveform or "N/A" in Phase 3)
 *   - First seen, last seen (ISO 8601 -> local date/time display)
 *   - Outcome, resolution time (resolved_at - ts in seconds)
 *
 * IDs, fingerprints, timestamps, and RSSI values use monospace font.
 * Numbers use tabular-nums for alignment.
 */
import type { AlertDetail } from './schemas';
import * as styles from './MetadataSidebar.css';

/** Props for MetadataSidebar. */
interface Props {
  /** Full alert detail with all evidence fields. */
  alert: AlertDetail;
}

/**
 * Format an ISO 8601 datetime string to a human-readable local date/time.
 *
 * @param iso - ISO 8601 string or null
 * @returns Formatted local date/time string or "N/A"
 */
function formatDatetime(iso: string | null | undefined): string {
  if (!iso) return 'N/A';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return iso;
  }
}

/**
 * Compute resolution time in seconds.
 *
 * @param ts - Alert open time (ISO 8601)
 * @param resolvedAt - Alert close time (ISO 8601) or null
 * @returns Formatted resolution time or "N/A"
 */
function formatResolutionTime(ts: string, resolvedAt: string | null): string {
  if (!resolvedAt) return 'N/A';
  const diffMs = new Date(resolvedAt).getTime() - new Date(ts).getTime();
  if (diffMs < 0) return 'N/A';
  const seconds = Math.round(diffMs / 1000);
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs}s`;
}

/**
 * Compute min/max/median RSSI from the waveform array.
 *
 * Returns null for all values when the array is empty (Phase 3 default).
 */
function rssiSummary(waveform: AlertDetail['rssi_waveform']) {
  const data = waveform ?? [];
  if (data.length === 0) return { min: null, max: null, median: null };
  const values = data.map((p) => p.rssi_dbm).sort((a, b) => a - b);
  const min = values[0];
  const max = values[values.length - 1];
  const mid = Math.floor(values.length / 2);
  const median = values.length % 2 === 0
    ? Math.round((values[mid - 1] + values[mid]) / 2)
    : values[mid];
  return { min, max, median };
}

/**
 * Severity badge with color-coded background.
 */
function SeverityBadge({ severity }: { severity: string }) {
  const className =
    severity === 'critical'
      ? styles.badgeCritical
      : severity === 'warning'
        ? styles.badgeWarning
        : styles.badgeInfo;
  return (
    <span className={className} data-testid="severity-badge">
      {severity}
    </span>
  );
}

/**
 * Outcome label with icon prefix.
 */
function OutcomeLabel({ outcome }: { outcome: AlertDetail['outcome'] }) {
  if (!outcome) return <span className={styles.dd}>unresolved</span>;
  const labels: Record<string, string> = {
    turned_away: 'turned away',
    entered: 'entered',
    loitering: 'loitering',
    other: 'other',
  };
  return <span className={styles.dd}>{labels[outcome] ?? outcome}</span>;
}

/**
 * Metadata definition list for all AlertDetail fields.
 *
 * Monospace font is applied to identifiers, fingerprints, timestamps,
 * and numeric values (RSSI, confidence, alert ID).
 */
export function MetadataSidebar({ alert }: Props) {
  const rssi = rssiSummary(alert.rssi_waveform);

  return (
    <section className={styles.sidebar} data-testid="metadata-sidebar">
      <h2 className={styles.heading}>Event Details</h2>
      <dl className={styles.dl}>
        <dt className={styles.dt}>Alert ID</dt>
        <dd className={styles.ddMono} data-testid="meta-alert-id">#{alert.id}</dd>

        <dt className={styles.dt}>Type</dt>
        <dd className={styles.dd} data-testid="meta-type">{alert.alert_type}</dd>

        <dt className={styles.dt}>Severity</dt>
        <dd className={styles.dd}><SeverityBadge severity={alert.severity} /></dd>

        <dt className={styles.dt}>Zone</dt>
        <dd className={styles.dd} data-testid="meta-zone">
          {alert.zone_name ?? 'Site-level'}
          {alert.zone_id !== null && (
            <span className={styles.ddMono} style={{ marginLeft: '6px' }}>
              (#{alert.zone_id})
            </span>
          )}
        </dd>

        <dt className={styles.dt}>Fingerprint</dt>
        <dd className={styles.ddMono} data-testid="meta-fingerprint">
          {alert.fingerprint ?? 'unknown'}
        </dd>

        <dt className={styles.dt}>Device type</dt>
        <dd className={styles.dd} data-testid="meta-device-type">
          {alert.device_type ?? 'unknown'}
        </dd>

        <dt className={styles.dt}>Confidence</dt>
        <dd className={styles.ddMono} data-testid="meta-confidence">
          {alert.confidence !== null && alert.confidence !== undefined
            ? `${Math.round(alert.confidence * 100)}%`
            : 'N/A'}
        </dd>

        <dt className={styles.dt}>RSSI min</dt>
        <dd className={styles.ddMono} data-testid="meta-rssi-min">
          {rssi.min !== null ? `${rssi.min} dBm` : 'N/A'}
        </dd>

        <dt className={styles.dt}>RSSI max</dt>
        <dd className={styles.ddMono} data-testid="meta-rssi-max">
          {rssi.max !== null ? `${rssi.max} dBm` : 'N/A'}
        </dd>

        <dt className={styles.dt}>RSSI median</dt>
        <dd className={styles.ddMono} data-testid="meta-rssi-median">
          {rssi.median !== null ? `${rssi.median} dBm` : 'N/A'}
        </dd>

        <dt className={styles.dt}>First seen</dt>
        <dd className={styles.ddMono} data-testid="meta-first-seen">
          {formatDatetime(alert.first_seen)}
        </dd>

        <dt className={styles.dt}>Last seen</dt>
        <dd className={styles.ddMono} data-testid="meta-last-seen">
          {formatDatetime(alert.last_seen)}
        </dd>

        <dt className={styles.dt}>Alert fired</dt>
        <dd className={styles.ddMono} data-testid="meta-ts">
          {formatDatetime(alert.ts)}
        </dd>

        <dt className={styles.dt}>Resolved at</dt>
        <dd className={styles.ddMono} data-testid="meta-resolved-at">
          {formatDatetime(alert.resolved_at)}
        </dd>

        <dt className={styles.dt}>Outcome</dt>
        <dd className={styles.dd} data-testid="meta-outcome">
          <OutcomeLabel outcome={alert.outcome} />
        </dd>

        <dt className={styles.dt}>Resolution time</dt>
        <dd className={styles.ddMono} data-testid="meta-resolution-time">
          {formatResolutionTime(alert.ts, alert.resolved_at)}
        </dd>

        {alert.chip_id && (
          <>
            <dt className={styles.dt}>Sensor</dt>
            <dd className={styles.ddMono} data-testid="meta-chip-id">{alert.chip_id}</dd>
          </>
        )}

        {alert.message && (
          <>
            <dt className={styles.dt}>Message</dt>
            <dd className={styles.dd} data-testid="meta-message">{alert.message}</dd>
          </>
        )}
      </dl>
    </section>
  );
}
